import { AppConfig } from "../config/AppConfig.js";

export class ApiCache {
  static cachePrefix = "api_cache_v1:";
  static memoryCache = new Map(); // key -> { expiresAt, data }
  static inFlight = new Map();    // key -> Promise

  // ============================
  // TTL Helpers
  // ============================
  static parseTTL(ttl) {
    if (!ttl) return 0;

    if (typeof ttl === "number") return ttl;

    if (typeof ttl === "string") {
      const m = ttl.trim().match(/^(\d+)\s*(ms|s|m|h|d)$/i);
      if (!m) throw new Error(`TTL inválido: ${ttl}`);

      const value = parseInt(m[1], 10);
      const unit = m[2].toLowerCase();

      const multipliers = {
        ms: 1,
        s: 1000,
        m: 60 * 1000,
        h: 60 * 60 * 1000,
        d: 24 * 60 * 60 * 1000,
      };

      return value * multipliers[unit];
    }

    return 0;
  }

  // ============================
  // Key builder
  // ============================
  static buildKey({ method, url, data, token }) {
    const tokenKey = token ? `t:${token}` : "t:public";

    let bodyKey = "";
    if (data) {
      if (data instanceof FormData) {
        bodyKey = "formdata";
      } else {
        bodyKey = JSON.stringify(data);
      }
    }

    return `${method}|${url}|${tokenKey}|${bodyKey}`;
  }

  static getStorageKey(cacheKey) {
    return `${this.cachePrefix}${cacheKey}`;
  }

  // ============================
  // Read / Write
  // ============================
  static read(cacheKey) {
    const now = Date.now();

    // 1) Memory
    const mem = this.memoryCache.get(cacheKey);
    if (mem && mem.expiresAt > now) {
      return mem.data;
    }

    // 2) localStorage
    try {
      const raw = localStorage.getItem(this.getStorageKey(cacheKey));
      if (!raw) return null;

      const parsed = JSON.parse(raw);

      if (!parsed?.expiresAt || parsed.expiresAt <= now) {
        localStorage.removeItem(this.getStorageKey(cacheKey));
        return null;
      }

      // rehidrato memoria
      this.memoryCache.set(cacheKey, { expiresAt: parsed.expiresAt, data: parsed.data });

      return parsed.data;
    } catch {
      localStorage.removeItem(this.getStorageKey(cacheKey));
      return null;
    }
  }

  static write(cacheKey, data, ttlMs, persistent = true) {
    const expiresAt = Date.now() + ttlMs;

    // Memory
    this.memoryCache.set(cacheKey, { expiresAt, data });

    // localStorage
    if (persistent) {
      try {
        localStorage.setItem(
          this.getStorageKey(cacheKey),
          JSON.stringify({ expiresAt, data })
        );
      } catch (e) {
        if (AppConfig.features?.debug) {
          console.warn("No se pudo guardar cache en localStorage:", e);
        }
      }
    }
  }

  // ============================
  // Invalidate
  // ============================
  static invalidate({ urlContains = "", token = null } = {}) {
    const tokenKey = token ? `t:${token}` : null;

    // Memory
    for (const key of this.memoryCache.keys()) {
      if (key.includes(urlContains) && (!tokenKey || key.includes(tokenKey))) {
        this.memoryCache.delete(key);
      }
    }

    // localStorage
    try {
      const keysToDelete = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (!k || !k.startsWith(this.cachePrefix)) continue;

        const cacheKey = k.replace(this.cachePrefix, "");
        if (cacheKey.includes(urlContains) && (!tokenKey || cacheKey.includes(tokenKey))) {
          keysToDelete.push(k);
        }
      }
      keysToDelete.forEach((k) => localStorage.removeItem(k));
    } catch {}
  }

  // ============================
  // Dedupe (in-flight)
  // ============================
  static getInFlight(cacheKey) {
    return this.inFlight.get(cacheKey) || null;
  }

  static setInFlight(cacheKey, promise) {
    this.inFlight.set(cacheKey, promise);
  }

  static clearInFlight(cacheKey) {
    this.inFlight.delete(cacheKey);
  }

  // ============================
  // Decide if we can cache
  // ============================
  static canCache({ ttlMs, data }) {
    if (!ttlMs || ttlMs <= 0) return false;
    if (data instanceof FormData) return false;
    return true;
  }
}
